import { AssetDebtFields, AssetsDebts, ChevronLeftIcon, ConsentOrdersPathName } from "@fla/components";
import { CheckCircleIcon } from "@heroicons/react/20/solid";
import classNames from "classnames";
import { selectConsentOrdersBase } from "pages/ConsentOrders";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

const restrictPaths = [
  ConsentOrdersPathName.RealEstate,
  ConsentOrdersPathName.Superannuation,
  ConsentOrdersPathName.Bank,
  ConsentOrdersPathName.Business,
  ConsentOrdersPathName.Vehicle,
  ConsentOrdersPathName.Investments,
  ConsentOrdersPathName.Overseas,
  ConsentOrdersPathName.Furniture,
  ConsentOrdersPathName.Debts,
  ConsentOrdersPathName.AdditionalRequirements,
  ConsentOrdersPathName.Summary,
  ConsentOrdersPathName.Submit
];

interface IProps {
  setOpen: any;
  basePath?: string;
  name: string;
  path: ConsentOrdersPathName;
  match: ConsentOrdersPathName[];
  children?: { name: string; step: number }[];
  subRoutes?: { name: string; path: ConsentOrdersPathName; field: keyof AssetDebtFields }[];

  assetsDebts: AssetsDebts;
  consentOrderSteps: {
    passedSteps: number[];
    currentStep: number;
  };
  setConsentOrderStep: (targetStep: number) => void;
  setAssetsDebtsStep: (targetStep: number) => void;
}

export const MenuList = ({
  consentOrderSteps,
  assetsDebts,
  basePath,
  name,
  path,
  children,
  subRoutes,
  setConsentOrderStep,
  setAssetsDebtsStep,
  setOpen,
  match
}: IProps) => {
  const { pathname } = useLocation() as { pathname: ConsentOrdersPathName };
  const navigateTo = useNavigate();
  const [collapse, setCollapse] = useState(false);

  const _consentOrders = useSelector(selectConsentOrdersBase);

  const isInitialDetailsCompleted = _consentOrders.initialDetails?.isCompleted;

  const isRouteMatched =
    (Array.isArray(match) && match.some(v => pathname.includes(v))) || (basePath && pathname.includes(basePath));

  /**
   * collapse if current is diff.
   */
  useEffect(() => {
    if (basePath && pathname.includes(basePath)) setCollapse(true);
    else setCollapse(false);
  }, [pathname]);

  return (
    <li className="w-full overflow-y-auto">
      <button
        type="button"
        onClick={() => {
          setCollapse(!collapse);

          if (path === ConsentOrdersPathName.Submit && !_consentOrders.assetsDebts?.summary?.isCompleted) return;
          if (restrictPaths.includes(path) && !isInitialDetailsCompleted) {
            return;
          } else if (!match.includes(pathname) && isInitialDetailsCompleted) {
            navigateTo(path);
          } else {
            if (typeof setOpen === "function") setOpen(false);

            if (pathname.includes("real-estate")) return;

            if (!Array.isArray(subRoutes) || !Array.isArray(children)) navigateTo(path);
          }
        }}
        className={classNames(
          "text-black flex justify-between items-center w-full px-8 py-5 rounded-lg lg:px-4 lg:py-2 text-left md:text-2xl lg:text-base",
          {
            "bg-[#dedede] text-black font-semibold": isRouteMatched,
            "cursor-not-allowed text-gray-400":
              (restrictPaths.includes(path) && !isInitialDetailsCompleted) ||
              (path === ConsentOrdersPathName.Submit && !_consentOrders?.assetsDebts?.summary?.isCompleted)
          }
        )}
      >
        <p>{name}</p>
        {children?.length || subRoutes?.length ? (
          <ChevronLeftIcon
            classes={classNames("w-3 transition-all transform", {
              "rotate-90": collapse,
              "rotate-180": !collapse
            })}
          />
        ) : null}
      </button>
      {collapse && children ? (
        <div>
          <span />
          {children.map((c, idx) => (
            <button
              type="button"
              className={classNames("pl-10 lg:pl-6 px-4 py-2 md:py-3 lg:py-2 text-gray-400 text-sm flex items-center", {
                "cursor-not-allowed":
                  !consentOrderSteps?.passedSteps?.includes(idx + 1) && consentOrderSteps.currentStep !== idx + 1
              })}
              key={idx}
              onClick={() => {
                setConsentOrderStep(idx + 1);
                if (typeof setOpen === "function") setOpen(false);
              }}
              disabled={!consentOrderSteps?.passedSteps?.includes(idx + 1)}
              tabIndex={idx}
            >
              <div className="w-7 md:w-10 lg:w-7 h-auto">
                {consentOrderSteps?.passedSteps?.includes(idx + 1) ? (
                  <CheckCircleIcon className="w-5 h-5 md:w-8 md:h-8 lg:w-6 lg:h-full -ml-0.5" color="#2F9D5E" />
                ) : (
                  <span
                    className={classNames(
                      "w-5 h-5 md:w-8 md:h-8 lg:w-5 lg:h-5 bg-primary text-white text-[10px] md:text-lg lg:text-[10px] rounded-full flex justify-center items-center",
                      {
                        "bg-opacity-40 text-blue-400":
                          !consentOrderSteps?.passedSteps?.includes(idx + 1) &&
                          consentOrderSteps.currentStep !== idx + 1
                      }
                    )}
                  >
                    {idx + 1}
                  </span>
                )}
              </div>
              <div className="w-full lg:w-32">
                <p
                  className={classNames("text-left md:text-xl lg:text-xs md:ml-7 lg:ml-1", {
                    "text-gray-400":
                      !consentOrderSteps?.passedSteps?.includes(idx + 1) && consentOrderSteps.currentStep !== idx + 1,
                    "text-black":
                      consentOrderSteps?.passedSteps?.includes(idx + 1) || consentOrderSteps.currentStep === idx + 1,
                    "font-semibold": consentOrderSteps?.currentStep === idx + 1
                  })}
                >
                  {c.name}
                </p>
              </div>
            </button>
          ))}
        </div>
      ) : null}

      {collapse && subRoutes
        ? subRoutes.map((c, idx) => (
            <button
              type="button"
              className={classNames("pl-10 lg:pl-6 px-4 py-2 md:py-3 lg:py-2 text-gray-400 text-sm flex items-center", {
                "cursor-not-allowed": !assetsDebts[c.field]?.isCompleted && assetsDebts?.currentStep !== idx + 1
              })}
              key={idx}
              onClick={() => {
                if (c.path === pathname) return;
                setAssetsDebtsStep(idx + 1);
                // if (typeof setOpen === "function") setOpen(false);
                navigateTo(c.path);
              }}
              disabled={!assetsDebts[c.field]?.isCompleted && assetsDebts?.currentStep !== idx + 1}
              tabIndex={idx}
            >
              <div className="w-7 md:w-10 lg:w-7 h-auto">
                {assetsDebts[c.field]?.isCompleted ? (
                  <CheckCircleIcon className="w-5 h-5 md:w-8 md:h-8 lg:w-6 lg:h-full -ml-0.5" color="#2F9D5E" />
                ) : (
                  <span
                    className={classNames(
                      "w-5 h-5 md:w-8 md:h-8 lg:w-5 lg:h-5 bg-primary text-white text-xs md:text-lg lg:text-xs rounded-full flex justify-center items-center",
                      {
                        // "border-2 border-green-500":
                        //   assetsDebts?.currentStep === idx + 1,
                        // // pathname === c.path,
                        "bg-opacity-40 text-blue-400":
                          !assetsDebts[c.field]?.isCompleted && assetsDebts?.currentStep !== idx + 1
                      }
                    )}
                  >
                    {idx + 1}
                  </span>
                )}
              </div>
              <div className="w-full lg:w-32">
                <p
                  className={classNames("text-left md:text-xl lg:text-sm md:ml-7 lg:ml-1", {
                    "text-gray-400": !assetsDebts[c.field]?.isCompleted && assetsDebts?.currentStep !== idx + 1,
                    "text-black": assetsDebts?.currentStep === idx + 1 || assetsDebts[c.field]?.isCompleted,
                    "font-semibold":
                      // assetsDebts?.currentStep === idx + 1 ||
                      pathname === c.path
                  })}
                >
                  {c.name}
                </p>
              </div>
            </button>
          ))
        : null}
    </li>
  );
};
